import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  ArrowRight,
  MapPin,
  Users,
  Award,
  Heart,
  Target,
  Lightbulb,
  CheckCircle,
  Star,
  Briefcase
} from 'lucide-react';

// Skills Progress Component
const SkillProgress = ({ skill, percentage, delay }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(percentage);
    }, delay);
    return () => clearTimeout(timer);
  }, [percentage, delay]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography 
          variant="body1" 
          color="#1A1A1A" 
          fontWeight={600}
          sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
        >
          {skill}
        </Typography>
        <Typography 
          variant="body2" 
          color="#FF5722" 
          fontWeight={600}
          sx={{ fontFamily: '"Inter", "Helvetica", "Arial", sans-serif' }}
        >
          {percentage}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 0,
          backgroundColor: '#E0E0E0',
          '& .MuiLinearProgress-bar': {
            borderRadius: 0,
            background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          },
        }}
      />
    </Box>
  );
};

// Value Card Component
const ValueCard = ({ icon: Icon, title, description, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%', 
          textAlign: 'center',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={32} />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666"
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ name, role, experience, specialties, delay }) => {
  return (
    <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
          }
        }}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 3,
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              fontSize: '2rem',
              fontWeight: 'bold',
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {name.split(' ').map(n => n[0]).join('')}
          </Avatar>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {name}
          </Typography>
          
          <Typography 
            variant="subtitle1" 
            color="#FF5722" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {role}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="#666" 
            sx={{ 
              mb: 3,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {experience} Years Experience
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {specialties.map((specialty, index) => (
              <Chip
                key={index}
                label={specialty}
                size="small"
                sx={{
                  background: 'rgba(255,87,34,0.1)',
                  color: '#FF5722',
                  border: '1px solid rgba(255,87,34,0.2)',
                  borderRadius: 0,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Zoom>
  );
};

// Timeline Item Component
const TimelineItem = ({ year, title, description, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Box sx={{ display: 'flex', mb: 4, position: 'relative' }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            mr: 3,
            flexShrink: 0,
            boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
          }}
        >
          {year}
        </Box>
        
        <Box>
          <Typography 
            variant="h6" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body1" 
            color="#666"
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

// Stats Card Component
const StatsCard = ({ number, label, icon: Icon, delay }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const end = parseInt(number);
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const counter = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(counter);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(counter);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [number, delay]);

  return (
    <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            color: 'white',
            boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
          }}
        >
          <Icon size={32} />
        </Box>
        
        <Typography 
          variant="h3" 
          component="div" 
          color="#FF5722" 
          sx={{ 
            fontWeight: 700, 
            mb: 1,
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
          }}
        >
          {count}{number.includes('+') ? '+' : ''}
        </Typography>
        
        <Typography 
          variant="h6" 
          color="#1A1A1A"
          sx={{ 
            fontWeight: 600,
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
          }}
        >
          {label}
        </Typography>
      </Box>
    </Zoom>
  );
};

const AboutUsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const skills = [
    { skill: 'Interior Design', percentage: 95 },
    { skill: 'Space Planning', percentage: 90 },
    { skill: '3D Visualization', percentage: 88 },
    { skill: 'Project Management', percentage: 92 },
    { skill: 'Brand Integration', percentage: 85 },
    { skill: 'Sustainable Design', percentage: 87 }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Passion for Design',
      description: 'We pour our heart into every project, creating spaces that truly inspire and delight our clients.'
    },
    {
      icon: Users,
      title: 'Client-Centric Approach',
      description: 'Your vision is our mission. We listen, understand, and translate your dreams into reality.'
    },
    {
      icon: Award,
      title: 'Excellence in Craftsmanship',
      description: 'We maintain the highest standards in every detail, ensuring exceptional quality in all our work.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation & Creativity',
      description: 'We push boundaries and explore new ideas to create unique, cutting-edge design solutions.'
    },
    {
      icon: CheckCircle,
      title: 'Trust & Transparency',
      description: 'We build lasting relationships through honest communication and reliable project delivery.'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'We focus on delivering measurable outcomes that exceed expectations and add real value.'
    }
  ];

  const teamMembers = [
    {
      name: 'Priya Sharma',
      role: 'Founder & Creative Director',
      experience: 15,
      specialties: ['Luxury Residential', 'Brand Integration', 'Sustainable Design']
    },
    {
      name: 'Rajesh Kumar',
      role: 'Senior Design Architect',
      experience: 12,
      specialties: ['Commercial Design', 'Space Planning', '3D Modeling']
    },
    {
      name: 'Anjali Reddy',
      role: 'Interior Design Specialist',
      experience: 10,
      specialties: ['Hospitality Design', 'Color Theory', 'Furniture Selection']
    },
    {
      name: 'Vikram Singh',
      role: 'Project Manager',
      experience: 8,
      specialties: ['Project Coordination', 'Client Relations', 'Quality Control']
    }
  ];

  const timeline = [
    {
      year: '2018',
      title: 'Company Founded in Hyderabad',
      description: 'Started our journey with a small passionate team and a big vision to transform interior design in India.'
    },
    {
      year: '2019',
      title: 'First Major Project (Jubilee Hills)',
      description: 'Completed our breakthrough luxury residential project that established our reputation in the market.'
    },
    {
      year: '2020',
      title: 'Team Expansion',
      description: 'Grew our team of talented designers and expanded our service offerings to include commercial projects.'
    },
    {
      year: '2021',
      title: 'Commercial Success',
      description: 'Successfully delivered 50+ projects and received recognition for innovative workspace designs.'
    },
    {
      year: '2022',
      title: 'Award Recognition',
      description: 'Received multiple design awards and established partnerships with leading furniture and decor brands.'
    },
    {
      year: '2024',
      title: 'Innovation Focus (3D/VR)',
      description: 'Integrated cutting-edge 3D visualization and VR technology to enhance client experience.'
    }
  ];

  const stats = [
    { number: '500', label: 'Projects Completed', icon: Briefcase },
    { number: '6', label: 'Years Experience', icon: Award },
    { number: '98', label: 'Client Satisfaction', icon: Star },
    { number: '50', label: 'Years Combined Expertise', icon: Users }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={8}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <MapPin size={16} style={{ marginRight: 8, color: '#FF5722' }} />
                    <Typography 
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Founded in Hyderabad
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Crafting{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        position: 'relative',
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Spaces
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Creating{' '}
                      <Box 
                        component="span" 
                        sx={{ 
                          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        Memories
                      </Box>
                    </Box>
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      maxWidth: 600, 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    We are a passionate team of designers and architects from Hyderabad who came together 
                    with decades of combined expertise to transform how people experience interior spaces.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 6 }}>
                    <Button 
                      component={RouterLink}
                      to="/contact"
                      variant="contained" 
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Work With Us
                    </Button>
                    
                    <Button 
                      component={RouterLink}
                      to="/portfolio"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      See Our Work
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box sx={{ position: 'relative' }}>
                  <Grid container spacing={2}>
                    {skills.slice(0, 6).map((skill, index) => (
                      <Grid item xs={12} key={index}>
                        <SkillProgress 
                          skill={skill.skill} 
                          percentage={skill.percentage} 
                          delay={500 + index * 200} 
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          background: '#FFFFFF',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements}>
                <Box>
                  <Typography 
                    variant="overline" 
                    color="#999" 
                    sx={{ 
                      mb: 2, 
                      display: 'block',
                      fontWeight: 500,
                      letterSpacing: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Our Story
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h2" 
                    color="#1A1A1A" 
                    sx={{ 
                      mb: 4,
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    From Vision to Reality
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="#666" 
                    paragraph
                    sx={{ 
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Founded in the vibrant city of Hyderabad in 2018, VectoSpaceDesign began as a small 
                    team of passionate designers who shared a common vision: to create spaces that don't 
                    just look beautiful, but transform the way people live, work, and feel.
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="#666" 
                    paragraph
                    sx={{ 
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Our journey started with a simple belief that great design has the power to improve 
                    lives. Over the years, we've grown from a startup to a comprehensive design studio, 
                    but our core values remain unchanged: passion, integrity, and an unwavering commitment 
                    to excellence.
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="#666" 
                    paragraph
                    sx={{ 
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Today, we bring together decades of combined expertise in interior design, 
                    architecture, and project management to deliver extraordinary results for our clients 
                    across residential, commercial, and hospitality sectors.
                  </Typography>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                {stats.map((stat, index) => (
                  <Box key={index} sx={{ flex: '1 1 200px', minWidth: 150 }}>
                    <StatsCard {...stat} delay={300 * (index + 1)} />
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Values Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography 
                  variant="overline" 
                  color="#999" 
                  sx={{ 
                    mb: 2, 
                    display: 'block',
                    fontWeight: 500,
                    letterSpacing: 3,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Our Values
                </Typography>
                <Typography 
                  variant="h2" 
                  component="h2" 
                  color="#1A1A1A" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  What Drives Us
                </Typography>
                <Typography 
                  variant="h6" 
                  component="p" 
                  color="#666" 
                  sx={{ 
                    maxWidth: 700, 
                    mx: 'auto',
                    fontWeight: 400,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Our core values guide every decision we make and every project we undertake.
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ValueCard {...value} delay={300 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Expert Team Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography 
                  variant="overline" 
                  color="#999" 
                  sx={{ 
                    mb: 2, 
                    display: 'block',
                    fontWeight: 500,
                    letterSpacing: 3,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Expert Team
                </Typography>
                <Typography 
                  variant="h2" 
                  component="h2" 
                  color="#1A1A1A" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Meet Our Designers
                </Typography>
                <Typography 
                  variant="h6" 
                  component="p" 
                  color="#666" 
                  sx={{ 
                    maxWidth: 700, 
                    mx: 'auto',
                    fontWeight: 400,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Our talented team brings together diverse expertise and creative vision.
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <TeamMemberCard {...member} delay={300 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Company Journey Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements}>
                <Box>
                  <Typography 
                    variant="overline" 
                    color="#999" 
                    sx={{ 
                      mb: 2, 
                      display: 'block',
                      fontWeight: 500,
                      letterSpacing: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Company Journey
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h2" 
                    color="#1A1A1A" 
                    sx={{ 
                      mb: 4,
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Our Growth Story
                  </Typography>
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666" 
                    sx={{ 
                      mb: 6,
                      fontWeight: 400,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    From our humble beginnings to becoming a leading design studio in Hyderabad, 
                    here's how we've evolved over the years.
                  </Typography>
                  
                  <Box>
                    {timeline.map((item, index) => (
                      <TimelineItem key={index} {...item} delay={300 * (index + 1)} />
                    ))}
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 600,
                    borderRadius: 0,
                    background: '#FFFFFF',
                    border: '1px solid #E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                      borderRadius: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      textAlign: 'center',
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Company Journey
                    <br />
                    Image Placeholder
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 30,
                      left: 30,
                      right: 30,
                      p: 3,
                      borderRadius: 0,
                      background: 'rgba(255,255,255,0.98)',
                      border: '1px solid rgba(255,255,255,0.8)'
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      color="#FF5722" 
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Hyderabad Headquarters
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="#1A1A1A"
                      sx={{
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Our design studio in the heart of Hyderabad, where creativity meets innovation.
                    </Typography>
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                component="h2" 
                color="white" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Ready to Start Your Design Journey?
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                color="white" 
                sx={{ 
                  mb: 6, 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Let's collaborate to create something extraordinary. We'd love to hear about your vision.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight />}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  Get In Touch
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/services"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Our Services
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUsPage;